/**
 * Свечные развороты.
 *
 * Одна свеча — самое старое, что есть в чтении графика, и самое
 * замусоренное: у каждой книги свои допуски, и «поглощение» у двух
 * авторов означает разное. Поэтому здесь не библиотека из пятидесяти
 * фигур, а четыре, которые определяются арифметикой бара без споров:
 * поглощение, пин-бар, внутренний бар, доджи.
 *
 * Каждая мера объявлена долей, а не пикселями и не абсолютной ценой:
 * «тень вдвое длиннее тела» одинаково верно на биткойне за девяносто
 * тысяч и на монете за три цента. Порог вынесен в параметр — у фигур
 * нет канонического значения, и делать вид, что есть, значило бы
 * выдать чужое соглашение за истину.
 *
 * Выход у каждой — событие: единица на баре, где фигура сложилась, ноль
 * в остальное время. Это тот же вид величины, что у пробоя структуры, и
 * читается он так же — «случилось», а не «выше нуля».
 */
function zeros(length) {
    return new Array(length).fill(0);
}
/** Тело свечи, всегда неотрицательное. */
function body(bar) {
    return Math.abs(bar.close - bar.open);
}
/** Полный размах свечи. */
function range(bar) {
    return bar.high - bar.low;
}
/**
 * Поглощение: свеча перекрывает телом тело предыдущей.
 *
 * Требуется противоположный цвет — иначе это просто продолжение
 * движения, а не смена намерения, ради которой фигуру и смотрят.
 * `factor` говорит, насколько тело должно быть больше предыдущего: 1
 * означает «хотя бы не меньше».
 */
export function engulfing(bars, factor) {
    const up = zeros(bars.length);
    const down = zeros(bars.length);
    for (let i = 1; i < bars.length; i += 1) {
        const bar = bars[i];
        const prev = bars[i - 1];
        if (!bar || !prev)
            continue;
        const own = body(bar);
        const before = body(prev);
        // Тело предыдущей свечи нулевое — поглощать нечего: любая свеча
        // «больше» доджи, и фигура нашлась бы на каждом втором баре.
        if (before <= 0 || own < before * factor)
            continue;
        const bull = bar.close > bar.open && prev.close < prev.open;
        const bear = bar.close < bar.open && prev.close > prev.open;
        if (bull && bar.close >= prev.open && bar.open <= prev.close)
            up[i] = 1;
        if (bear && bar.close <= prev.open && bar.open >= prev.close)
            down[i] = 1;
    }
    return { up, down };
}
/**
 * Пин-бар: длинная тень с одной стороны, тело прижато к другой.
 *
 * Читается как отказ рынка идти дальше: цену туда завели и вернули
 * внутри одной свечи. `factor` — во сколько раз тень длиннее тела.
 */
export function pinBar(bars, factor) {
    const up = zeros(bars.length);
    const down = zeros(bars.length);
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        if (!bar)
            continue;
        const full = range(bar);
        if (full <= 0)
            continue;
        const size = body(bar);
        const top = Math.max(bar.open, bar.close);
        const bottom = Math.min(bar.open, bar.close);
        const upper = bar.high - top;
        const lower = bottom - bar.low;
        /*
         * Тело меряется от размаха, а не от нуля: у свечи с нулевым телом
         * любое отношение к нему бесконечно, и доджи объявлялся бы
         * пин-баром в обе стороны сразу.
         */
        const need = Math.max(size, full * 0.05) * factor;
        if (lower >= need && upper <= lower / 2)
            up[i] = 1;
        if (upper >= need && lower <= upper / 2)
            down[i] = 1;
    }
    return { up, down };
}
/**
 * Внутренний бар: свеча целиком внутри предыдущей.
 *
 * Сжатие, а не направление, — поэтому у него один ряд, а не два.
 * Направление даёт то, что случится после: выход вверх или вниз.
 */
export function insideBar(bars) {
    const out = zeros(bars.length);
    for (let i = 1; i < bars.length; i += 1) {
        const bar = bars[i];
        const prev = bars[i - 1];
        if (!bar || !prev)
            continue;
        if (bar.high <= prev.high && bar.low >= prev.low)
            out[i] = 1;
    }
    return out;
}
/**
 * Доджи: тело почти отсутствует.
 *
 * Равновесие: открытие и закрытие сошлись, и кто победил — по свече не
 * видно. `share` — какую долю размаха телу позволено занимать.
 */
export function doji(bars, share) {
    const out = zeros(bars.length);
    for (let i = 0; i < bars.length; i += 1) {
        const bar = bars[i];
        if (!bar)
            continue;
        const full = range(bar);
        if (full <= 0)
            continue;
        if (body(bar) <= full * share)
            out[i] = 1;
    }
    return out;
}
//# sourceMappingURL=candles.js.map